import { useState, useEffect } from 'react';
import { formatRating } from '@/utils/formatters';
import { MapPin, Star, Clock, Compass, ChevronDown, ChevronUp, Map, Globe, Sparkles, Search, Heart, Zap, Share2, Check, RefreshCw, BookmarkPlus } from 'lucide-react';
import { getSharedTrip, type SavedItinerary, type SavedPlace } from '@/hooks/useItineraries';
import { staticMapUrl } from '@/utils/googleProxy';
import { translations, type Language } from '@/translations';
import type { Translations } from '@/translations';
import { ReevaluatePanel } from '@/components/ReevaluatePanel';
import { AuthModal } from '@/components/AuthModal';
import { useAuth } from '@/hooks/useAuth';
import { useSharedTripPaintWatch } from '@/hooks/useSharedTripPaintWatch';
import { writePendingReevaluate } from '@/lib/pendingReevaluateStore';
import { safeStorage } from '@/lib/safeStorage';
import { reportError } from '@/lib/reportError';
import type { ReevaluatedPlace, ReevaluateContext } from '@/services/reevaluate';

/** Same key the LanguageProvider uses (see src/hooks/useLanguage.tsx).
 *  When the anon viewer explicitly picks a flag we mirror their choice
 *  into localStorage so a subsequent sign-up + redirect to AppMain
 *  inherits the picked language via the Provider's first-mount read. */
const LANGUAGE_STORAGE_KEY = 'tripboom-language';

const WEBSITE_URL = 'https://www.tripboom.app';

const flags: { lang: Language; emoji: string; label: string }[] = [
  { lang: 'en', emoji: '🇬🇧', label: 'English' },
  { lang: 'fr', emoji: '🇫🇷', label: 'Français' },
  { lang: 'es', emoji: '🇪🇸', label: 'Español' },
  { lang: 'de', emoji: '🇩🇪', label: 'Deutsch' },
];

interface SharedTripPageProps {
  slug: string;
}

function SharedPlaceCard({ place, t }: { place: SavedPlace; t: Translations }) {
  const [expanded, setExpanded] = useState(false);
  const photo = place.photo_url || null;

  const today = new Date().getDay();
  // Supabase weekdayDescriptions is Mon-Sun (0-6), JS getDay() is Sun(0)-Sat(6)
  const todayIndex = today === 0 ? 6 : today - 1;
  const todayHours = place.opening_hours?.weekdayDescriptions?.[todayIndex];

  return (
    <div
      className="bg-[var(--bg-card)] rounded-xl border border-[var(--border)] overflow-hidden cursor-pointer transition-all hover:border-[var(--accent-teal)]/40 hover:bg-[var(--accent-teal)]/5 hover:shadow-md"
      onClick={() => setExpanded(!expanded)}
    >
      {/* Collapsed view */}
      <div className="flex gap-3 p-3">
        {photo && (
          <img
            src={photo}
            alt={place.name}
            loading="lazy"
            decoding="async"
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate pr-2">
              {place.name}
            </h3>
            {place.rating && (
              <span className="flex items-center gap-0.5 text-xs text-[var(--accent-gold)] flex-shrink-0">
                <Star className="w-3 h-3 fill-current" />
                {formatRating(place.rating)}
                {place.user_ratings_total > 0 && (
                  <span className="text-[var(--text-secondary)]">({place.user_ratings_total.toLocaleString()})</span>
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {place.category && (
              <span className="text-xs text-[var(--text-secondary)]">{place.category}</span>
            )}
            {place.estimated_duration && (
              <span className="flex items-center gap-0.5 text-xs text-[var(--text-secondary)]">
                <Clock className="w-3 h-3" />
                {place.estimated_duration}
              </span>
            )}
            {place.distance_note && (
              <span className="text-xs text-[var(--text-secondary)]">{place.distance_note}</span>
            )}
          </div>
          {place.hook && (
            <p className={`text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-line ${expanded ? '' : 'line-clamp-2'}`}>{place.hook}</p>
          )}
          {!expanded && (
            <div className="flex items-center gap-1 mt-1.5 text-xs text-[var(--accent-teal)]">
              <span>{t.common.showMore}</span>
              <ChevronDown className="w-3 h-3" />
            </div>
          )}
        </div>
      </div>

      {/* Expanded view */}
      {expanded && (
        <div className="border-t border-[var(--border)] p-3 space-y-3">
          {/* Map (cache-only: no Google API call if not pre-cached) */}
          {place.location_lat && place.location_lng && (
            <div className="w-full h-40 rounded-lg overflow-hidden">
              <img
                src={staticMapUrl(place.location_lat, place.location_lng, 15, '600x300', true)}
                alt="Map"
                className="w-full h-full object-cover"
                onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
              />
            </div>
          )}

          {/* Vibes */}
          {place.matching_vibes && place.matching_vibes.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {place.matching_vibes.map(vibe => {
                const label = vibe.startsWith('custom:') ? vibe.slice(7) : vibe;
                const isCustom = vibe.startsWith('custom:');
                return (
                  <span
                    key={vibe}
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      isCustom
                        ? 'bg-amber-500/25 text-[var(--accent-gold)] border border-amber-500/40'
                        : 'bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] border border-[var(--accent-teal)]/20'
                    }`}
                  >
                    {isCustom ? `✨ ${label}` : label}
                  </span>
                );
              })}
            </div>
          )}

          {/* Address */}
          {place.formatted_address && (
            <p className="text-xs text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">{t.results.address}:</span> {place.formatted_address}
            </p>
          )}

          {/* Opening hours */}
          {place.opening_hours?.weekdayDescriptions && (
            <div className="text-xs text-[var(--text-secondary)]">
              <span className="font-medium text-[var(--text-primary)]">{t.results.hoursToday}:</span>
              <div className="mt-0.5">{todayHours || 'N/A'}</div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {place.location_lat && place.location_lng && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${place.location_lat},${place.location_lng}&destination_place_id=${encodeURIComponent(place.place_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-teal)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-teal)] transition-colors flex items-center justify-center gap-1"
              >
                <Map className="w-4 h-4" />
                {t.common.navigate}
              </a>
            )}
            {place.website_uri && (
              <a
                href={place.website_uri}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex-1 py-2 px-3 rounded-lg bg-[var(--bg-elevated)] hover:bg-[var(--accent-gold)]/20 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-gold)] transition-colors flex items-center justify-center gap-1"
              >
                <Globe className="w-4 h-4" />
                {t.results.visitWebsite}
              </a>
            )}
          </div>

          <div className="flex items-center gap-1 text-xs text-[var(--accent-teal)]">
            <span>{t.common.showLess}</span>
            <ChevronUp className="w-3 h-3" />
          </div>
        </div>
      )}
    </div>
  );
}

export function SharedTripPage({ slug }: SharedTripPageProps) {
  const [trip, setTrip] = useState<SavedItinerary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [language, setLanguage] = useState<Language>('en');
  const [shareCopied, setShareCopied] = useState(false);

  // H-215 — anonymous-viewer Reevaluate state. AuthModal here is local
  // useState (NOT feedback.setAuthOpen) because SharedTripPage is mounted
  // in App.tsx:96 BEFORE <AppMain> wraps anything in <UiFeedbackProvider>.
  const [reevaluatePanelOpen, setReevaluatePanelOpen] = useState(false);
  const [reevaluatedPlaces, setReevaluatedPlaces] = useState<ReevaluatedPlace[] | null>(null);
  const [reevaluateContext, setReevaluateContext] = useState<ReevaluateContext | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const auth = useAuth();

  const t = translations[language];

  // Prevent browsers from serving stale cached version of a shared trip
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.httpEquiv = 'Cache-Control';
    meta.content = 'no-cache, no-store, must-revalidate';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    setLoading(true);
    setLoadFailed(false);
    getSharedTrip(slug)
      .then((result) => {
        if (result.status === 'found') {
          setTrip(result.itinerary);
          // Default to the sharer's language, fallback to English
          const shareLang = (result.itinerary as SavedItinerary & { share_language?: string }).share_language;
          if (shareLang && shareLang in translations) {
            setLanguage(shareLang as Language);
          }
        } else if (result.status === 'error') {
          setLoadFailed(true);
        }
      })
      .catch((err: unknown) => {
        reportError('sharedTrip.load', err, { slug, step: 'unexpected' });
        setLoadFailed(true);
      })
      .finally(() => setLoading(false));
  }, [slug, attempt]);

  /** Wrap setLanguage so an EXPLICIT user pick (header flag or modal lang
   *  picker) is mirrored into localStorage. The auto-set from the share's
   *  share_language at line ~222 deliberately does NOT call this — opening
   *  someone else's French link shouldn't permanently flip the viewer's
   *  app default until they actively engage. */
  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    safeStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  };

  // After auth success while AuthModal is open, leave SharedTripPage so the
  // App.tsx post-auth materialiser (gated on isAuthenticated + sessionReady)
  // can drain pendingReevaluateStore. SharedTripPage doesn't host the
  // materialiser itself — AppMain does — so we have to navigate away.
  useEffect(() => {
    if (authModalOpen && auth.user) {
      window.location.assign('/');
    }
  }, [authModalOpen, auth.user]);

  const titleRef = useSharedTripPaintWatch(loading, trip?.places.length ?? null);

  const handleReevaluated = (results: ReevaluatedPlace[], context: ReevaluateContext) => {
    setReevaluatedPlaces(results);
    setReevaluateContext(context);
    setReevaluatePanelOpen(false);
  };

  const handleSaveReevaluated = () => {
    if (!reevaluatedPlaces || !reevaluateContext) return;
    // Stash for the post-auth materialiser in App.tsx, then open AuthModal.
    writePendingReevaluate(slug, reevaluateContext, reevaluatedPlaces);
    setAuthModalOpen(true);
  };

  const handleClearReevaluation = () => {
    setReevaluatedPlaces(null);
    setReevaluateContext(null);
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    const shareData = {
      title: trip?.title || trip?.location || 'TripBoom Trip',
      text: `${trip?.title || trip?.location || 'Trip'} — ${trip?.places.length ?? 0} places. Check it out on TripBoom:`,
      url: shareUrl,
    };

    await new Promise(r => setTimeout(r, 100));

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch { /* user cancelled or failed */ }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--accent-teal)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)] text-sm">{t.sharedTrip.loadingTrip}</p>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Compass className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4 opacity-40" />
          <h1 className="text-xl font-semibold tracking-tight text-[var(--text-primary)] mb-2">{t.sharedTrip.loadFailed}</h1>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            {t.sharedTrip.loadFailedDesc}
          </p>
          <button
            onClick={() => setAttempt((n) => n + 1)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-all"
          >
            <RefreshCw className="w-4 h-4" />
            {t.sharedTrip.retry}
          </button>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <Compass className="w-12 h-12 text-[var(--text-secondary)] mx-auto mb-4 opacity-40" />
          <h1 className="text-xl font-semibold tracking-tight text-[var(--text-primary)] mb-2">{t.sharedTrip.tripNotFound}</h1>
          <p className="text-sm text-[var(--text-secondary)] mb-6">
            {t.sharedTrip.tripNotFoundDesc}
          </p>
          <a
            href={WEBSITE_URL}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black font-semibold hover:shadow-lg transition-all"
          >
            {t.sharedTrip.ctaButton}
          </a>
        </div>
      </div>
    );
  }

  const heroPlace = trip.places[0];
  const heroPhoto = heroPlace?.photo_url || null;

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[var(--bg-primary)]/95 backdrop-blur-md border-b border-[var(--border)] pt-[env(safe-area-inset-top)]">
        <div className="flex items-center justify-between px-4 py-2.5 max-w-2xl mx-auto">
          <a href={WEBSITE_URL} className="flex items-center gap-1.5">
            <span className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
              Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span>
            </span>
          </a>
          <div className="flex items-center gap-2">
            {/* Language flags */}
            <div className="flex items-center gap-0.5">
              {flags.map(({ lang, emoji, label }) => (
                <button
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={`w-6 h-6 rounded flex items-center justify-center text-sm transition-all ${
                    language === lang
                      ? 'bg-[var(--accent-teal)]/15 ring-1 ring-[var(--accent-teal)]/40 scale-110'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  aria-label={label}
                  title={label}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <a
              href="/"
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black hover:shadow-lg transition-all"
            >
              App
            </a>
            <button
              onClick={handleShare}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-primary)] hover:border-[var(--accent-teal)]/40 hover:text-[var(--accent-teal)] active:scale-95 transition-all"
              aria-label="Share this trip"
            >
              {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              {shareCopied ? 'Copied!' : 'Share'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative h-56 overflow-hidden">
        {heroPhoto ? (
          <img
            src={heroPhoto}
            alt={trip.location}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Replace broken image with gradient fallback
              const img = e.target as HTMLImageElement;
              img.style.display = 'none';
              img.parentElement!.insertAdjacentHTML('afterbegin', '<div class="w-full h-full bg-gradient-to-br from-[var(--accent-teal)]/20 to-[var(--accent-gold)]/20 absolute inset-0"></div>');
            }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[var(--accent-teal)]/20 to-[var(--accent-gold)]/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h1 ref={titleRef} className="text-2xl font-semibold tracking-tight text-white mb-1">{trip.title || trip.location}</h1>
          <div className="flex items-center gap-3 text-white/80 text-sm">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {trip.location}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {t.time[trip.time_available as keyof typeof t.time] || trip.time_available}
            </span>
          </div>
          <p className="text-white/60 text-xs mt-1">
            {t.sharedTrip.placesCount.replace('{count}', String(trip.places.length))}
          </p>
        </div>
      </div>

      {/* Reevaluate prompt banner — full-width, gradient, scrolls with the
          page (NOT sticky/fixed) so it never steals viewport on small screens.
          Hides once the user has reevaluated (the result banner takes over). */}
      {!reevaluatedPlaces && (
        <button
          onClick={() => setReevaluatePanelOpen(true)}
          className="w-full bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black active:scale-[0.99] transition-transform"
          aria-label={t.reevaluate?.heroBannerCta || t.reevaluate?.headerButton || 'Reevaluate these places for your context'}
        >
          <div className="max-w-2xl mx-auto px-4 py-3.5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Sparkles className="w-5 h-5 flex-shrink-0" />
              <div className="text-left min-w-0">
                <p className="text-sm font-semibold leading-tight">
                  {t.reevaluate?.heroBannerTitle || 'Reevaluate these places for your context'}
                </p>
                <p className="text-xs opacity-80 leading-tight mt-0.5 truncate">
                  {t.reevaluate?.heroBannerSubtitle || 'Re-rank for where you are now, how you’re getting around, and how long you have'}
                </p>
              </div>
            </div>
            <RefreshCw className="w-4 h-4 flex-shrink-0" />
          </div>
        </button>
      )}

      {/* Reevaluation result banner */}
      {reevaluatedPlaces && reevaluateContext && (
        <div className="px-4 max-w-2xl mx-auto pt-4">
          <div className="rounded-xl border border-[var(--accent-teal)]/30 bg-[var(--accent-teal)]/10 p-3 sm:p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[var(--accent-teal)]">
                  {reevaluatedPlaces.some(p => p.rankingSource === 'offline') ? t.reevaluate.offlineBannerTitle : t.reevaluate.bannerTitle}
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  {(reevaluatedPlaces.some(p => p.rankingSource === 'offline') ? t.reevaluate.offlineBannerSubtitle : t.reevaluate.bannerSubtitle)
                    .replace('{transport}', t.transport[reevaluateContext.transport as keyof typeof t.transport] || reevaluateContext.transport)
                    .replace('{time}', t.time[reevaluateContext.timeAvailable as keyof typeof t.time] || reevaluateContext.timeAvailable)}
                </p>
              </div>
              <button
                onClick={handleClearReevaluation}
                className="text-xs px-2 py-1 rounded-md text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] flex-shrink-0"
              >
                {t.reevaluate?.clear || 'Reset'}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleSaveReevaluated}
                className="flex-1 py-2.5 rounded-lg bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black font-semibold text-sm flex items-center justify-center gap-1.5 hover:shadow-md transition-all"
              >
                <BookmarkPlus className="w-4 h-4" />
                {t.reevaluate?.saveCta || 'Save this trip to my account'}
              </button>
              <button
                onClick={() => setReevaluatePanelOpen(true)}
                className="flex-1 sm:flex-initial py-2.5 px-3 rounded-lg border border-[var(--accent-teal)]/40 bg-[var(--accent-teal)]/10 text-[var(--accent-teal)] font-semibold text-sm flex items-center justify-center gap-1.5 hover:bg-[var(--accent-teal)]/20 transition-all"
                aria-label={t.reevaluate?.headerButton || 'Reevaluate again'}
              >
                <RefreshCw className="w-4 h-4" />
                {t.reevaluate?.headerButton || 'Reevaluate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Place list */}
      <div className="p-4 space-y-3 max-w-2xl mx-auto">
        {(reevaluatedPlaces
          ? reevaluatedPlaces.map((r) => ({
              ...r.source,
              hook: r.hook,
              distance_note: r.distanceNote,
            }))
          : trip.places
        ).map((place, i) => (
          <SharedPlaceCard key={place.id || i} place={place} t={t} />
        ))}
      </div>

      {/* About TripBoom section */}
      <div className="mt-6 px-4 max-w-2xl mx-auto">
        <div className="rounded-2xl bg-gradient-to-br from-[var(--bg-card)] to-[var(--bg-elevated)] border border-[var(--border)] p-6 space-y-5">
          <div className="text-center">
            <p className="text-lg font-semibold tracking-tight text-[var(--text-primary)]">
              Trip<span style={{ background: 'linear-gradient(to right, #f59e0b, #2dd4bf)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Boom</span>
            </p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t.sharedTrip.aboutTagline}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--bg-primary)]">
              <Search className="w-4 h-4 text-[var(--accent-teal)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-primary)]">{t.sharedTrip.featureSearch}</p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t.sharedTrip.featureSearchDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--bg-primary)]">
              <Sparkles className="w-4 h-4 text-[var(--accent-gold)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-primary)]">{t.sharedTrip.featureAI}</p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t.sharedTrip.featureAIDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--bg-primary)]">
              <Heart className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-primary)]">{t.sharedTrip.featureSave}</p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t.sharedTrip.featureSaveDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[var(--bg-primary)]">
              <Zap className="w-4 h-4 text-[var(--accent-gold)] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-primary)]">{t.sharedTrip.featureFree}</p>
                <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t.sharedTrip.featureFreeDesc}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-2">
            <a
              href="/"
              className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#2dd4bf] to-[#f59e0b] text-black font-semibold text-center text-base hover:shadow-lg hover:shadow-teal-500/25 transition-all hover:-translate-y-0.5"
            >
              {t.sharedTrip.ctaButton}
            </a>
            <div className="flex items-center gap-3 text-[10px] text-[var(--text-secondary)]/60">
              <a href={`${WEBSITE_URL}/features`} className="hover:text-[var(--accent-teal)] transition-colors">{t.sharedTrip.linkFeatures}</a>
              <span>·</span>
              <a href={`${WEBSITE_URL}/pricing`} className="hover:text-[var(--accent-teal)] transition-colors">{t.sharedTrip.linkPricing}</a>
              <span>·</span>
              <a href={`${WEBSITE_URL}/install`} className="hover:text-[var(--accent-teal)] transition-colors">{t.sharedTrip.linkInstall}</a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-6 mb-4 text-center">
        <p className="text-[10px] text-[var(--text-secondary)]/40">
          {t.sharedTrip.madeWith}
        </p>
      </footer>

      {/* H-215 Reevaluate panel */}
      {reevaluatePanelOpen && (
        <ReevaluatePanel
          trip={trip}
          language={language}
          t={t}
          onClose={() => setReevaluatePanelOpen(false)}
          onReevaluated={handleReevaluated}
          onLanguageChange={handleLanguageChange}
          previousContext={reevaluateContext}
        />
      )}

      {/* H-215 AuthModal — local to SharedTripPage (outside UiFeedbackProvider). */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSignIn={auth.signIn}
        onSignUp={auth.signUp}
        onGoogleSignIn={auth.signInWithGoogle}
        onResetPassword={auth.resetPassword}
        onEmailOtp={auth.signInWithEmailOtp}
        onVerifyEmailOtp={auth.verifyEmailOtp}
      />
    </div>
  );
}
